import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchRodLiftAnalysisCardCoordinatesAsync,
  fetchRodLiftAnalysisCardCoordinatesBatchAsync,
  startBatchOperation,
} from '../../features/rod-lift-analysis/CardCoordinatesSlice';
import { fetchRodLiftAnalysisAsync } from '../../features/rod-lift-analysis/AnalysisSlice';
import './CardViewer.scss';
import CardTableView from './card-table-view/CardTableView';
import Dynamometer from '../../features/card-viewer/Dynamometer';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  CardDateItemState,
  resetCardDateListState,
  toggleAllSelected,
  toggleSelected,
} from '../../features/rod-lift-analysis/CardDateSlice';
import Contextualizer from '../../features/contextualizer/Contextualizer';
import SimpleModal from '../common/simple-modal/SimpleModal';
import AnalysisInputOutput from '../../features/common/AnalysisInputOutput/AnalysisInputOutput';
import XDAnalysis from '../../features/common/XDAnalysis/XDAnalysis';
import { ReactComponent as ArrowSquareRightIcon } from '../../images/arrow-square-right.svg';
import StreamingMonitor from '../../features/rod-lift-analysis/StreamingMonitor';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import { syncAxes as syncAxesUtil } from '../../utilities/chartHelpers';
import { XYChart } from '@amcharts/amcharts5/xy';

interface UrlParams {
  assetId: string;
}

interface CardViewerProps {
  timeseriesChartData?: any;
  layout?: string;
  accessible?: boolean;
  externalChartRefs?: React.MutableRefObject<(XYChart | null)[]>;
  onChartRegistered?: (index: number, chart: XYChart) => void;
}

export interface LegendItemType {
  id: string;
  label: string;
  color: string;
  hasImage: boolean;
  imageUrl?: string;
}
const contextualizerLegendData: LegendItemType[] = [
  {
    id: 'startup',
    label: 'Startup',
    color: '#4294FF',
    hasImage: false,
  },
  {
    id: 'shutdown',
    label: 'Shutdown',
    color: '#f04438',
    hasImage: false,
  },
  {
    id: 'pump-off',
    label: 'Pump off',
    color: '#ffbf3f',
    hasImage: false,
  },
  {
    id: 'current',
    label: 'Current',
    color: '#6bbbae',
    hasImage: false,
  },
];
const cardAnalyticsLegendData: LegendItemType[] = [
  {
    id: 'uplift-opportunity',
    label: 'Uplift opportunity',
    color: '#12B76A',
    hasImage: false,
  },
  {
    id: 'rods-overloaded',
    label: 'Rods overloaded',
    color: '#FECDCA',
    hasImage: false,
  },
  {
    id: 'pump-tagging',
    label: 'Pump tagging',
    color: '#F97066',
    hasImage: false,
  },
  {
    id: 'gearbox-overloaded',
    label: 'Gearbox overloaded',
    color: '#F8AA62',
    hasImage: false,
  },
  {
    id: 'standing-valve-leak',
    label: 'Standing valve leak',
    color: '#C471F3',
    hasImage: false,
  },
];

const CardViewer: React.FC<CardViewerProps> = ({ externalChartRefs, onChartRegistered }) => {
  const [minDate, setMinDate] = useState<Date>(new Date());
  const [maxDate, setMaxDate] = useState<Date>(new Date());
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Chart refs for syncing axes between Contextualizer charts and external TimeSeriesCharts
  const amCharRefs = useRef<(XYChart | null)[]>([]);

  const dispatch = useAppDispatch();
  const cardDateStore = useAppSelector((state) => state.rodLiftAnalysis);
  const cardCoordinatesStore = useAppSelector((state) => state.rodLiftAnalysisCardCoordinates);
  const analysisStore = useAppSelector((state) => state.rodLiftAnalysisAnalysis);

  const { assetId } = useParams<keyof UrlParams>() as UrlParams;

  // Chart syncing functions
  const addAmChartRef = (index: number, chart: XYChart) => {
    if (chart) {
      amCharRefs.current[index] = chart;
      // Register with shared refs for cross-component syncing
      if (onChartRegistered) {
        onChartRegistered(index, chart);
      }
    }
  };

  // Create combined chart refs for syncing (includes both Contextualizer and external charts)
  const getCombinedChartRefs = (): (XYChart | null)[] => {
    const combinedRefs = [...amCharRefs.current];
    if (externalChartRefs?.current) {
      combinedRefs.push(...externalChartRefs.current);
    }
    return combinedRefs;
  };

  const syncAxes = (chart: any) => {
    const combinedRefs = { current: getCombinedChartRefs() };
    syncAxesUtil(combinedRefs, chart);
  };

  useEffect(() => {
    dispatch(resetCardDateListState());
  }, [dispatch]);

  const onCardDateItemClickHandler = (_: number, date: string) => {
    const cardItem = cardDateStore.values.find((x) => x.date === date);

    // If the card is currently selected, we're deselecting it - no need to fetch data
    const isCurrentlySelected = cardItem?.isSelected === true;

    // Toggle the selection first
    dispatch(toggleSelected(date));

    // Only fetch data if the card was not selected and we don't already have the data
    if (!isCurrentlySelected) {
      const hasCoordinatesData = cardCoordinatesStore.values[date] !== undefined;
      const hasAnalysisData = analysisStore.values[date] !== undefined;

      // Fetch coordinates data if we don't have it
      if (!hasCoordinatesData) {
        dispatch(fetchRodLiftAnalysisCardCoordinatesAsync({ assetId: assetId, cardDate: date }));
      }

      // Fetch analysis data if we don't have it
      if (!hasAnalysisData) {
        dispatch(fetchRodLiftAnalysisAsync({ assetId: assetId, cardDate: date }));
      }
    }
  };

  const onCardDateBatchClickHandler = (datesToFetch: string[]) => {
    fetchCardDataFormMultipleSelectedDates(datesToFetch);
    dispatch(toggleAllSelected(datesToFetch));
  };

  // Replica handler for multiple dates
  const fetchCardDataFormMultipleSelectedDates = (dates: string[]) => {
    // Filter out dates that are already selected
    const datesToFetch = dates.filter((date) => !cardDateStore.values.find((x) => x.date === date)?.isSelected);

    const alreadySelectedDates = cardDateStore.values.filter((x) => x.isSelected).map((x) => x.date);
    if (datesToFetch.length > ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE - alreadySelectedDates.length) {
      return; // Exceeds maximum selectable cards
    }

    if (datesToFetch.length === 0) {
      return; // No new dates to fetch
    }

    // Process in batches of 50 dates as per API limitation
    const BATCH_SIZE = 50;
    const batches = [];

    for (let i = 0; i < datesToFetch.length; i += BATCH_SIZE) {
      const batch = datesToFetch.slice(i, i + BATCH_SIZE);
      batches.push(batch);
    }

    // Start batch operation tracking
    dispatch(startBatchOperation({ totalBatches: batches.length }));

    // Dispatch all batch requests
    batches.forEach((batch) => {
      dispatch(
        fetchRodLiftAnalysisCardCoordinatesBatchAsync({
          assetId: assetId,
          cardDates: batch,
        }),
      );
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<CardDateItemState>();

  const handleViewCardAnalysis = async (rowData: CardDateItemState) => {
    setSelectedRowData(rowData);
    try {
      await Promise.all([
        dispatch(fetchRodLiftAnalysisCardCoordinatesAsync({ assetId, cardDate: rowData.date })).unwrap(),
        dispatch(fetchRodLiftAnalysisAsync({ assetId, cardDate: rowData.date })).unwrap(),
      ]);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch analysis or coordinates:', error);
    }
  };

  const getSelectedAnalysisInput = () => {
    if (!selectedRowData) {
      return null;
    }
    return analysisStore.values[selectedRowData.date]?.input.map((x, index) => ({
      key: index,
      id: x.id,
      title: x.displayName,
      value: x.displayValue,
    }));
  };

  const getSelectedAnalysisOutput = () => {
    if (!selectedRowData) {
      return null;
    }
    return analysisStore.values[selectedRowData.date]?.output.map((x, index) => ({
      key: index,
      id: x.id,
      title: x.displayName,
      value: x.displayValue,
    }));
  };

  const getSelectedXDiagAnalysis = () => {
    if (!selectedRowData) {
      return '';
    }
    return analysisStore.values[selectedRowData.date]?.xdiagAnalysis.displayValue;
  };

  return (
    <main className='main-container'>
      <StreamingMonitor />
      <div>
        <div className='card-viewer flex-row'>
          <div className={`dynamometer-container w-[487px]${isCollapsed ? ' collapse-container' : ''}`}>
            <button className='collapse-btn mt-[1px]' onClick={() => setIsCollapsed((prev) => !prev)}>
              <ArrowSquareRightIcon width={24} height={24} />
            </button>
            <Dynamometer
              setMinDate={setMinDate}
              setMaxDate={setMaxDate}
              startDate={minDate}
              endDate={maxDate}
              cardDateItems={cardDateStore.values}
              onClick={onCardDateItemClickHandler}
              cardTypeNames={[]} // Removed unused state
            />
          </div>
          <div className='card-data-table-container'>
            <CardTableView
              cardDateItems={cardDateStore.values}
              rowSelectionClick={onCardDateItemClickHandler}
              rowBactchSelectionClick={onCardDateBatchClickHandler}
              cardTypeNames={[]} // Removed unused state
              viewCardAnalysis={handleViewCardAnalysis}
            />
          </div>
        </div>
        <div className='contextualizer-container'>
          <Contextualizer
            cardDateItems={cardDateStore.values}
            fetchCardDataFormMultipleSelectedDates={fetchCardDataFormMultipleSelectedDates}
            isCardAnalytics={false}
            legendsData={contextualizerLegendData}
            chartTitle='Card contextualizer'
            addAmChartRef={addAmChartRef}
            chartIndex={0}
            syncAxes={syncAxes}
            isStreamingComplete={cardDateStore.streamingComplete}
          />
        </div>
        <div className='card-analytics-container'>
          <Contextualizer
            cardDateItems={cardDateStore.values}
            fetchCardDataFormMultipleSelectedDates={fetchCardDataFormMultipleSelectedDates}
            isCardAnalytics={true}
            legendsData={cardAnalyticsLegendData}
            chartTitle='Card analytics'
            addAmChartRef={addAmChartRef}
            chartIndex={1}
            syncAxes={syncAxes}
            isStreamingComplete={cardDateStore.streamingComplete}
          />
        </div>
      </div>
      {isModalOpen && selectedRowData && (
        <SimpleModal header='Analysis' onClose={() => setIsModalOpen(false)}>
          <div className='flex gap-[10px]'>
            <AnalysisInputOutput headerText='Input' values={getSelectedAnalysisInput()} loading={false} />
            <AnalysisInputOutput headerText='Output' values={getSelectedAnalysisOutput()} loading={false} />
            <XDAnalysis headerText='Analysis data' value={getSelectedXDiagAnalysis()} loading={false} />
          </div>
        </SimpleModal>
      )}
    </main>
  );
};

export default CardViewer;
